import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../api';
import { appNameWithVersion } from '../version';

const ResetPassword = ({ token, onResetSuccess }) => {
  const { language } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [accountLabel, setAccountLabel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      setValidating(true);
      try {
        const result = await api.validateResetToken(token);
        if (result.success && result.valid) {
          setValid(true);
          setAccountLabel(result.email || result.username || '');
        } else {
          setValid(false);
          setError(result.message || 'This reset link is invalid or has expired.');
        }
      } catch (err) {
        setValid(false);
        setError(err.response?.data?.message || 'This reset link is invalid or has expired.');
      } finally {
        setValidating(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setValidating(false);
      setValid(false);
      setError('Reset token is missing.');
    }
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(language === 'nl' ? 'Wachtwoord moet minimaal 6 tekens zijn' : 'Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError(language === 'nl' ? 'Wachtwoorden komen niet overeen' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await api.resetPassword(token, password);
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onResetSuccess?.();
        }, 1500);
      } else {
        setError(result.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          mt: 8,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/salinator-logo.png"
          alt="Salinator Logo"
          sx={{ maxWidth: '154px', width: '70%', mb: 2 }}
        />

        <Typography variant="h6" component="h2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
          {appNameWithVersion}
        </Typography>

        <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
          {language === 'nl' ? 'Nieuw wachtwoord' : 'Set New Password'}
        </Typography>

        {validating ? (
          <CircularProgress sx={{ my: 4 }} />
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                {success}
              </Alert>
            )}

            {valid && !success && (
              <>
                {accountLabel && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    {language === 'nl' ? 'Account:' : 'Account:'} {accountLabel}
                  </Typography>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={language === 'nl' ? 'Nieuw wachtwoord' : 'New Password'}
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label={language === 'nl' ? 'Bevestig wachtwoord' : 'Confirm Password'}
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : language === 'nl' ? (
                      'Wachtwoord opslaan'
                    ) : (
                      'Save Password'
                    )}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPassword;
